import { useTranslation } from 'react-i18next'
import { Alert, Button, CircularProgress, Stack, Typography } from '@mui/material'

import { isNewerVersion } from '@/core/classes/MedconnectDevicesAPI'

import { ConnectorHealthState } from '../../model/useConnectorHealth'

export type ConnectorCheckStepProps = {
  health: ConnectorHealthState
  release?: { currentVersion: string | null; latestVersion: string; downloadUrl?: string }
}

/** First step: is the local connector running, up to date, with Bluetooth on. */
export default function ConnectorCheckStep({ health, release }: ConnectorCheckStepProps) {
  const { t } = useTranslation()

  if (!health.isSettled) {
    return (
      <Stack alignItems="center" spacing={2} py={4}>
        <CircularProgress size={28} />
        <Typography color="text.secondary">{t('page.configuration.MedconnectDevice.wizard.connector.checking')}</Typography>
      </Stack>
    )
  }

  if (health.isLegacy) {
    return (
      <Stack spacing={2}>
        <Alert severity="warning">{t('page.configuration.MedconnectDevice.wizard.connector.outdated')}</Alert>
        <Typography color="text.secondary">{t('page.configuration.MedconnectDevice.wizard.connector.outdatedHint')}</Typography>
        {release?.downloadUrl && (
          <Button variant="contained" href={release.downloadUrl} target="_blank" rel="noreferrer">
            {t('page.configuration.MedconnectDevice.wizard.connector.download')}
          </Button>
        )}
      </Stack>
    )
  }

  if (health.isUnreachable || !health.health) {
    return (
      <Stack spacing={2}>
        <Alert severity="error">{t('page.configuration.MedconnectDevice.wizard.connector.unreachable')}</Alert>
        <Typography color="text.secondary">{t('page.configuration.MedconnectDevice.wizard.connector.unreachableHint')}</Typography>
        {release?.downloadUrl && (
          <Button variant="outlined" href={release.downloadUrl} target="_blank" rel="noreferrer">
            {t('page.configuration.MedconnectDevice.wizard.connector.download')}
          </Button>
        )}
      </Stack>
    )
  }

  // `bluetooth` is typed non-optional but older connectors omit it: guard against undefined as well as null.
  const bluetooth = health.health.bluetooth
  const isBluetoothOff = bluetooth != null && bluetooth.state !== 'poweredOn'
  const hasUpdate = !!release?.currentVersion && isNewerVersion(release.latestVersion, release.currentVersion)

  return (
    <Stack spacing={2}>
      <Alert severity="success">{t('page.configuration.MedconnectDevice.wizard.connector.reachable')}</Alert>
      {isBluetoothOff && <Alert severity="warning">{t('page.configuration.MedconnectDevice.wizard.connector.bluetoothOff')}</Alert>}
      {hasUpdate && (
        <Alert
          severity="info"
          action={
            release.downloadUrl ? (
              <Button color="inherit" size="small" href={release.downloadUrl} target="_blank" rel="noreferrer">
                {t('page.configuration.MedconnectDevice.wizard.connector.download')}
              </Button>
            ) : undefined
          }
        >
          {t('page.configuration.MedconnectDevice.wizard.connector.updateAvailable', { current: release?.currentVersion, latest: release?.latestVersion })}
        </Alert>
      )}
      <Typography color="text.secondary">{t('page.configuration.MedconnectDevice.wizard.connector.readyHint')}</Typography>
    </Stack>
  )
}
